import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAlert } from "react-alert";
import Select from "react-select";

import { useDispatch, useSelector } from "react-redux";
import { getAllExamTypes } from "../../../actions/commonActions";
import { getSubscriptions } from "../../../actions/adminSubscriptionActions";
import { getStudents } from "../../../actions/adminStudentActions";
import {
  addStudentSubscription,
  clearErrors,
} from "../../../actions/adminSubscriptionActions";
import { ADD_STUDENT_SUBSCRIPTION_RESET } from "../../../constants/adminSubscriptionConstants";

import Loader from "../../Loader";

const AddStudentSubscription = ({ history }) => {
  const alert = useAlert();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    userId: "",
    subscriptionId: "",
    examId: "",
    universityId: "",
  });

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);

  const { userId, subscriptionId, examId, universityId } = formData;

  const { isStudentSubAdded, addingSubscription, error } = useSelector(
    (state) => state.subscriptions
  );

  const { allExamTypes, eloading } = useSelector((state) => state.commonExams);
  const { subscriptions, user_loading: subsLoading } = useSelector(
    (state) => state.subscriptions
  );
  const { students, user_loading: studentsLoading } = useSelector(
    (state) => state.students
  );

  useEffect(() => {
    dispatch(getAllExamTypes());
    dispatch(getSubscriptions());
    dispatch(getStudents());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      alert.error(error);
      dispatch(clearErrors());
    }

    if (isStudentSubAdded) {
      alert.success("Subscription successfully assigned to student.");
      history.push("/admin/student-subscriptions");
      dispatch({
        type: ADD_STUDENT_SUBSCRIPTION_RESET,
      });
    }
  }, [dispatch, isStudentSubAdded, alert, error, history]);

  // Prepare options for select dropdowns
  const studentOptions = Array.isArray(students)
    ? students.map((student) => ({
        value: student._id,
        label: `${student.first_name} ${student.last_name} (${student.email})`,
      }))
    : [];

  const examOptions = Array.isArray(allExamTypes)
    ? allExamTypes.map((exam) => ({
        value: exam._id,
        label: exam.title,
      }))
    : [];

  const subscriptionOptions = Array.isArray(subscriptions)
    ? subscriptions.map((sub) => ({
        value: sub._id,
        label: `${sub.title} - ₦${sub.price} (${sub.duration} ${sub.subscriptionType})`,
        examType: sub.examType?._id,
      }))
    : [];

  // Filter subscriptions based on selected exam
  const filteredSubscriptionOptions = selectedExam
    ? subscriptionOptions.filter(
        (sub) => !sub.examType || sub.examType === selectedExam.value
      )
    : subscriptionOptions;

  const handleStudentChange = (selected) => {
    setSelectedStudent(selected);
    setFormData({ ...formData, userId: selected?.value || "" });
  };

  const handleExamChange = (selected) => {
    setSelectedExam(selected);
    setFormData({ ...formData, examId: selected?.value || "" });
    // Reset subscription when exam changes
    setSelectedSubscription(null);
    setFormData((prev) => ({ ...prev, subscriptionId: "" }));
  };

  const handleSubscriptionChange = (selected) => {
    setSelectedSubscription(selected);
    setFormData({ ...formData, subscriptionId: selected?.value || "" });
  };

  const submitHandler = (e) => {
    e.preventDefault();

    if (!userId) {
      return alert.error("Please select a student");
    }
    if (!examId) {
      return alert.error("Please select an exam");
    }
    if (!subscriptionId) {
      return alert.error("Please select a subscription plan");
    }

    dispatch(addStudentSubscription(userId, subscriptionId, examId, universityId));
  };

  const isLoading = eloading || subsLoading || studentsLoading;

  return (
    <Fragment>
      {isLoading ? (
        <Loader />
      ) : (
        <Fragment>
          <div className="content-wrapper">
            <section className="content-header">
              <h1>
                Assign
                <small>Subscription to Student</small>
              </h1>
              <ol className="breadcrumb">
                <li>
                  <Link to="/admin">
                    <i className="fa fa-dashboard"></i> Dashboard
                  </Link>
                </li>
                <li>
                  <Link to="/admin/student-subscriptions">
                    <i className="fa fa-credit-card"></i> Student Subscriptions
                  </Link>
                </li>
                <li className="active">Add Subscription</li>
              </ol>
            </section>

            <section className="content">
              <div className="box box-default">
                <div className="box-header with-border">
                  <h3 className="box-title">Assign Subscription to Student</h3>
                </div>

                <form id="quickForm" onSubmit={submitHandler}>
                  <div className="box-body">
                    <div className="form-group row">
                      <div className="col-md-6">
                        <label htmlFor="student">Select Student *</label>
                        <Select
                          name="student"
                          value={selectedStudent}
                          onChange={handleStudentChange}
                          options={studentOptions}
                          placeholder="Search and select a student..."
                          isClearable
                          isSearchable
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="exam">Select Exam *</label>
                        <Select
                          name="exam"
                          value={selectedExam}
                          onChange={handleExamChange}
                          options={examOptions}
                          placeholder="Select an exam..."
                          isClearable
                          isSearchable
                        />
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-md-6">
                        <label htmlFor="subscription">
                          Select Subscription Plan *
                        </label>
                        <Select
                          name="subscription"
                          value={selectedSubscription}
                          onChange={handleSubscriptionChange}
                          options={filteredSubscriptionOptions}
                          placeholder="Select a subscription plan..."
                          isClearable
                          isSearchable
                          isDisabled={!selectedExam}
                        />
                        {!selectedExam && (
                          <small className="text-muted">
                            Please select an exam first
                          </small>
                        )}
                      </div>
                    </div>

                    {selectedSubscription && (
                      <div className="form-group">
                        <div className="alert alert-info">
                          <strong>Selected Plan:</strong>{" "}
                          {selectedSubscription.label}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="box-footer">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={addingSubscription}
                    >
                      {addingSubscription ? "Assigning..." : "Assign Subscription"}
                    </button>
                    <Link
                      to="/admin/student-subscriptions"
                      className="btn btn-default"
                      style={{ marginLeft: "10px" }}
                    >
                      Cancel
                    </Link>
                  </div>
                </form>
              </div>
            </section>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default AddStudentSubscription;
